import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import type { FormsPortalRole } from './settingsPermissions';
import type { ApplicationFormDepartment, FormWorkflowConfig } from '../types/applicationForm';

export type { ApplicationFormDepartment };

export type FormAccessContext = {
  role: FormsPortalRole | string;
  departmentSlug: string | null;
};

export async function fetchApplicationFormDepartments(admin = false): Promise<ApplicationFormDepartment[]> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc(admin ? 'list_departments_admin' : 'list_departments');
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as ApplicationFormDepartment[];
}

export async function upsertApplicationFormDepartment(input: {
  id?: string | null;
  name: string;
  slug?: string;
  sort_order?: number;
  active?: boolean;
}): Promise<ApplicationFormDepartment> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('upsert_department', {
    p_id: input.id ?? null,
    p_name: input.name.trim(),
    p_slug: input.slug?.trim() || null,
    p_sort_order: input.sort_order ?? null,
    p_active: input.active ?? true,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return data as ApplicationFormDepartment;
}

export async function fetchFormWorkflow(formSlug = 'account-creation'): Promise<FormWorkflowConfig> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('get_form_workflow', { p_form_slug: formSlug });
  if (error) throw new Error(formatSupabaseError(error));
  return normalizeFormWorkflow(data);
}

export async function saveFormWorkflow(
  formSlug: string,
  workflow: FormWorkflowConfig,
): Promise<FormWorkflowConfig> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('save_form_workflow', {
    p_form_slug: formSlug,
    p_workflow: workflow,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return normalizeFormWorkflow(data);
}

export function normalizeFormWorkflow(raw: unknown): FormWorkflowConfig {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<FormWorkflowConfig>;
  return {
    submission: {
      public: Boolean(data.submission?.public ?? true),
      internal: Boolean(data.submission?.internal ?? true),
      department_slugs: [...(data.submission?.department_slugs ?? [])],
    },
    approval: {
      department_slugs: [...(data.approval?.department_slugs ?? [])],
    },
    notifications: {
      on_submit_department_slugs: [...(data.notifications?.on_submit_department_slugs ?? [])],
      on_approve_department_slugs: [...(data.notifications?.on_approve_department_slugs ?? [])],
    },
  };
}

const FIXED_APPROVER_ROLES = new Set<FormsPortalRole>(['it', 'management']);

export function canApproveForm(
  access: FormAccessContext,
  workflow: FormWorkflowConfig,
): boolean {
  const role = (access.role ?? 'user').toLowerCase() as FormsPortalRole;
  const dept = access.departmentSlug?.trim().toLowerCase();
  if (dept === 'it' || dept === 'management') return true;
  if (FIXED_APPROVER_ROLES.has(role)) return true;
  if (!dept) return false;
  return workflow.approval.department_slugs.some((slug) => slug.toLowerCase() === dept);
}

export function canSubmitInternalForm(
  access: FormAccessContext,
  workflow: FormWorkflowConfig,
  formSlug = 'account-creation',
): boolean {
  if (!workflow.submission.internal) return false;
  // Account Creation: any Forms portal user may submit (verifier/approver are separate).
  if (formSlug === 'account-creation') return true;
  const dept = access.departmentSlug?.trim().toLowerCase();
  if (!dept) return false;
  if (dept === 'it' || dept === 'management') return true;
  // Empty list = any allowlisted department may submit (e.g. CFF).
  if (workflow.submission.department_slugs.length === 0) return true;
  return workflow.submission.department_slugs.some((slug) => slug.toLowerCase() === dept);
}

export function canAccessFormSettings(
  role: string | null | undefined,
  departmentSlug?: string | null,
): boolean {
  const dept = departmentSlug?.trim().toLowerCase();
  if (dept === 'it' || dept === 'management') return true;
  const normalized = (role ?? 'user').trim().toLowerCase();
  return normalized === 'it' || normalized === 'management';
}

/**
 * Certificate Request is open to all Forms portal users (any department).
 * Soft-launch IT/Management gate removed — always allow.
 */
export function canAccessCertificateRequest(_access?: {
  elevatedDepartment?: string | null;
  departmentSlug?: string | null;
  formsRole?: string | null;
} | null): boolean {
  return true;
}
